<template>
  <PortalForm
    :portal-type="customerType"
    :title="title"
    :headerIconSrc="iconSrc"
    :current-step="props.currentStep"
    :steps="steps"
    @back="goBack"
    @step-change="handlePortalStepChange"
    :form-valid="props.isStepValid"
  >
    <!-- Success Message for form -->
    <FormSuccessMessage
      v-if="formSubmitted"
      :email="email" 
      @go-back="goBack"
    />
    
    <!-- Loading Skeleton when navigating between steps -->
    <FormSkeleton 
      v-else-if="isNavigating"
      :steps-count="steps.length"
      :fields-count="4"
    />
    
    <!-- Main Form Content -->
    <div 
      v-else 
      :class="[customerType === 'lender' ? 'card-lender' : 'card-customer']"
    >
      <!-- Saved Form Banner - only show if hasIncompleteForm is true -->
      <div 
        v-if="shouldShowIncompleteBanner && !formSubmitted" 
        class="saved-form-banner mb-4"
      >
        <div class="flex">
          <div class="flex-shrink-0">
            <Icon icon="mdi:history" class="h-5 w-5 text-amber-400" />
          </div>
          <div class="ml-3">
            <p class="text-sm">
              You have an incomplete form from {{ formattedLastUpdated || 'earlier' }}.
              <button 
                @click="restoreForm" 
                class="font-medium underline hover:text-amber-800"
              >
                Continue
              </button>
              <button 
                @click="shouldShowIncompleteBanner = false" 
                class="font-medium ml-2 hover:text-amber-800"
              >
                Dismiss
              </button>
            </p>
          </div>
        </div>
      </div>
      
      <!-- Enhanced Progress Steps - if there are multiple steps -->
      <FormProgressSteps 
        v-if="steps.length > 1 && !usingPortalForm"
        :steps="stepsWithDescriptions"
        :current-step="props.currentStep"
        active-color="blue"
        class="mb-6"
      />
      
      <!-- Main form content -->
      <transition name="form-section" mode="out-in">
        <slot></slot>
      </transition>
    </div>
  </PortalForm>
</template>

<script setup>
import { ref, reactive, computed, watchEffect, onMounted, watch } from 'vue';
import { useStorage, useTimeAgo } from '@vueuse/core';
import PortalForm from '@/components/common/PortalForm.vue';
import FormSuccessMessage from '@/components/common/FormSuccessMessage.vue';
import FormProgressSteps from '@/components/common/FormProgressSteps.vue';
import FormSkeleton from '@/components/common/FormSkeleton.vue';
import { Icon } from '@iconify/vue';

const props = defineProps({
  customerType: {
    type: String,
    default: 'customer',
    validator: value => ['customer', 'lender'].includes(value)
  },
  title: {
    type: String,
    required: true
  },
  iconSrc: {
    type: String,
    default: null
  },
  steps: {
    type: Array,
    required: true
  },
  currentStep: {
    type: Number,
    required: true
  },
  isStepValid: {
    type: Boolean,
    default: true
  },
  email: {
    type: String,
    default: ''
  },
  formId: {
    type: String,
    default: () => `form-${Math.random().toString(36).substring(2, 9)}`
  },
  usingPortalForm: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['navigate', 'reset', 'restore-form']);

// Form state
const formSubmitted = ref(false);
const isSubmitting = ref(false);
const isNavigating = ref(false);
const errors = reactive({});

// Add loading state between steps
const navigateWithTransition = async (nextStep) => {
  isNavigating.value = true;
  
  // Simulate loading for smoother UX (min 300ms)
  await new Promise(resolve => setTimeout(resolve, 300));
  
  // Instead of setting local currentStep, emit navigate
  // emit('navigate', nextStep); 
  // The parent (MortgageeChangeForm) will update its currentStep, which flows back as a prop.
  // The actual change of currentStep for display will happen when the prop updates.
  
  // We still need to know the target step to manage isNavigating
  // This part needs rethinking if parent controls currentStep solely via prop.
  // For now, we assume the parent will update the currentStep prop, and a watcher will handle isNavigating.

  // Let's simplify: The parent is responsible for changing the currentStep prop.
  // This function's role is now more about the visual transition.
  // However, direct emit('navigate') is better.
  // currentStep.value = nextStep; // This should not be here if currentStep is a prop
  
  setTimeout(() => {
    isNavigating.value = false;
  }, 100);
};

// Watch for prop changes to currentStep to handle navigation state
watch(() => props.currentStep, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    isNavigating.value = true;
    setTimeout(() => {
      isNavigating.value = false;
    }, 400); // Corresponds to transition + a bit
  }
});

// Handler for PortalForm's step-change event
const handlePortalStepChange = (newStep) => {
  emit('navigate', newStep); // Emit navigate to the parent
};

// Enhanced steps with descriptions
const stepsWithDescriptions = computed(() => {
  return props.steps.map(step => {
    // If step already has a description, use it
    if (step.description) return step;
    
    // Add default descriptions based on common step patterns
    let description = '';
    const title = step.title.toLowerCase();
    
    if (title.includes('contact') || title.includes('information')) {
      description = 'Your basic details';
    } else if (title.includes('review') || title.includes('submit')) {
      description = 'Confirm and submit';
    }
    
    return {
      ...step,
      description
    };
  });
});

// Form persistence with localStorage
const storageKey = computed(() => `form-${props.formId}-${props.customerType}`);

// Add a cleanup function to clear corrupted localStorage entries
const cleanupLocalStorage = () => {
  try {
    console.log('[PolicyChangeForm] Cleaning up potentially corrupted localStorage entries');
    // Get all form-related keys
    const formKeys = Object.keys(localStorage).filter(key => key.includes('form-'));
    formKeys.forEach(key => {
      try {
        const value = localStorage.getItem(key);
        // Check if it's the problematic [object Object] string literal
        if (value === '[object Object]') {
          console.log(`[PolicyChangeForm] Removing corrupted entry for key: ${key}`);
          localStorage.removeItem(key);
        } else {
          // Try to parse it as JSON to see if it's valid
          try {
            JSON.parse(value);
          } catch (e) {
            console.log(`[PolicyChangeForm] Removing invalid JSON entry for key: ${key}`);
            localStorage.removeItem(key);
          }
        }
      } catch (keyError) {
        console.error(`[PolicyChangeForm] Error processing localStorage key ${key}:`, keyError);
      }
    });
  } catch (e) {
    console.error('[PolicyChangeForm] Error cleaning up localStorage:', e);
  }
};

// Clean up localStorage before initializing
cleanupLocalStorage();

const formStorage = useStorage(
  storageKey.value, 
  {
    lastUpdated: null,
    data: {},
    currentStep: 1
  },
  localStorage, // Explicitly specify localStorage as the storage adapter
  {
    serializer: {
      read: (v) => {
        try {
          if (!v) return {
            lastUpdated: null,
            data: {},
            currentStep: 1
          };
          
          // Special case for the literal string "[object Object]" which is invalid JSON
          if (v === '[object Object]') {
            console.log('[PolicyChangeForm] Found literal string "[object Object]" in storage, returning default object');
            return {
              lastUpdated: null,
              data: {},
              currentStep: 1
            };
          }
          
          // Try to parse the value if it's a string
          const parsed = typeof v === 'string' ? JSON.parse(v) : v;
          console.log('[PolicyChangeForm] Successfully read and parsed storage value:', parsed);
          return parsed;
        } catch (e) {
          console.error('[PolicyChangeForm] Error reading storage:', e);
          // Remove the problematic key to prevent future errors
          try {
            localStorage.removeItem(storageKey.value);
            console.log(`[PolicyChangeForm] Removed problematic storage key: ${storageKey.value}`);
          } catch (removeError) {
            console.error('[PolicyChangeForm] Error removing problematic storage key:', removeError);
          }
          return {
            lastUpdated: null,
            data: {},
            currentStep: 1
          };
        }
      },
      write: (v) => {
        try {
          // Always serialize to JSON string for consistent storage
          const serialized = JSON.stringify(v);
          console.log('[PolicyChangeForm] Successfully serialized value for storage');
          return serialized;
        } catch (e) {
          console.error('[PolicyChangeForm] Error serializing for storage:', e);
          return JSON.stringify({
            lastUpdated: null,
            data: {},
            currentStep: 1
          });
        }
      }
    }
  }
);

const hasIncompleteForm = computed(() => {
  // First check if we have basics
  const hasBasics = formStorage.value.lastUpdated !== null && 
                   !formSubmitted.value && 
                   formStorage.value.data && 
                   typeof formStorage.value.data === 'object';
  
  // If we don't have basics, return false
  if (!hasBasics) return false;
  
  // Then check if data is actually populated
  const hasKeys = Object.keys(formStorage.value.data).length > 0;
  
  // Check if data has any real values
  const hasValues = Object.values(formStorage.value.data).some(value => {
    if (typeof value === 'string') return value.trim().length > 0;
    if (typeof value === 'object' && value !== null) return Object.keys(value).length > 0;
    return !!value; // handle other types
  });
  
  const isRealData = hasKeys && hasValues;
  if (!isRealData && hasKeys) {
    console.log('[PolicyChangeForm] Form storage has keys but no real values, ignoring');
  }
  
  return isRealData;
});

// Format the last updated time in a human-readable format
const formattedLastUpdated = computed(() => {
  try {
    // Check if lastUpdated exists and is valid
    if (!formStorage.value.lastUpdated) {
      console.warn('[PolicyChangeForm] Missing lastUpdated timestamp');
      return '';
    }
    
    const timestamp = formStorage.value.lastUpdated;
    console.log(`[PolicyChangeForm] Formatting timestamp: ${timestamp}`);
    
    // Verify it's a valid date before creating timeAgo
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      console.error(`[PolicyChangeForm] Invalid timestamp value: ${timestamp}`);
      return '';
    }
    
    const timeAgo = useTimeAgo(date);
    return timeAgo.value || '';
  } catch (error) {
    console.error('[PolicyChangeForm] Error formatting timestamp:', error);
    return '';
  }
});

// Add a computed property to control displaying the banner
const shouldShowIncompleteBanner = computed(() => {
  return hasIncompleteForm.value && 
         formattedLastUpdated.value !== '';
});

// Navigation methods
const goToNextStep = () => {
  // This method might be redundant if PortalForm handles its own next/prev buttons
  // and emits step-change, which is then emitted as 'navigate'.
  // Kept for now if it's called internally or exposed.
  // emit('navigate', { direction: 'next', currentStep: props.currentStep }); // Use prop
  // navigateWithTransition(props.currentStep + 1); // This was wrong
  emit('navigate', props.currentStep + 1);
};

const goToPreviousStep = () => {
  // if (props.currentStep > 1) { // Use prop
  // emit('navigate', { direction: 'previous', currentStep: props.currentStep }); // Use prop
  // navigateWithTransition(props.currentStep - 1); // This was wrong
  // }
  if (props.currentStep > 1) {
    emit('navigate', props.currentStep - 1);
  }
};

const goBack = () => {
  if (formSubmitted.value) {
    resetForm();
  } else if (props.currentStep > 1) { // Use prop
    // emit('navigate', { direction: 'previous', currentStep: props.currentStep }); // Use prop
    // navigateWithTransition(props.currentStep - 1); // This was wrong
    emit('navigate', props.currentStep - 1);
  } else {
    // Navigate back to the service selection page
    window.history.back();
  }
};

const resetForm = () => {
  formSubmitted.value = false;
  // navigateWithTransition(1); // This was wrong
  emit('navigate', 1); // Request parent to navigate to step 1
  
  // Clear errors
  Object.keys(errors).forEach(key => {
    errors[key] = '';
  });
  
  // Clear stored form data
  formStorage.value = {
    lastUpdated: null,
    data: {},
    currentStep: 1 // This should reflect the actual step, now controlled by parent
  };
  formStorage.value.currentStep = props.currentStep; // Align with prop on reset interaction
  
  emit('reset');
};

const restoreForm = () => {
  try {
    if (!hasIncompleteForm.value) {
      console.warn('[PolicyChangeForm] No incomplete form to restore');
      return;
    }
    
    console.log('[PolicyChangeForm] Attempting to restore incomplete form');
    console.log('[PolicyChangeForm] Raw formStorage.value for restore:', formStorage.value);
    console.log('[PolicyChangeForm] Storage type:', typeof formStorage.value);
    
    // Initialize a valid form data object
    let validFormData = null;
    
    // Analyze what we're working with
    let rawData = formStorage.value;
    const dataType = typeof rawData;
    const stringRepresentation = String(rawData);
    
    console.log('[PolicyChangeForm] Debug raw restore data:', {
      value: rawData,
      type: dataType,
      stringValue: stringRepresentation,
      objectToString: Object.prototype.toString.call(rawData),
      hasOwnProperty: rawData && (typeof rawData === 'object') ? Object.prototype.hasOwnProperty.call(rawData, 'data') : false,
      keys: rawData && (typeof rawData === 'object') ? Object.keys(rawData) : []
    });
    
    // Case 1: It's already a valid object
    if (dataType === 'object' && rawData !== null) {
      console.log('[PolicyChangeForm] Restore data is already an object, using directly');
      validFormData = rawData;
    }
    // Case 2: It's a JSON string
    else if (dataType === 'string' && 
             stringRepresentation !== '[object Object]' && 
             (stringRepresentation.startsWith('{') || stringRepresentation.startsWith('['))) {
      console.log('[PolicyChangeForm] Restore data appears to be a JSON string, attempting to parse');
      try {
        validFormData = JSON.parse(stringRepresentation);
        console.log('[PolicyChangeForm] Successfully parsed JSON string for restore:', validFormData);
      } catch (parseError) {
        console.error('[PolicyChangeForm] Failed to parse JSON string for restore:', parseError);
        return;
      }
    }
    // Case 3: Other types can't be restored
    else {
      console.warn(`[PolicyChangeForm] Restore data is a ${dataType}, cannot use for restore:`, rawData);
      return;
    }
    
    // Validate we have a proper object
    if (!validFormData || typeof validFormData !== 'object') {
      console.error('[PolicyChangeForm] Invalid restore data - not an object:', validFormData);
      return;
    }
    
    console.log('[PolicyChangeForm] Form storage keys:', Object.keys(validFormData));
    
    // Validate the saved step is valid
    const savedStep = validFormData.currentStep || 1;
    if (savedStep < 1 || savedStep > props.steps.length) {
      console.warn(`[PolicyChangeForm] Invalid step in storage (${savedStep}), defaulting to step 1`);
      emit('navigate', 1);
    } else {
      console.log(`[PolicyChangeForm] Restoring to step ${savedStep}`);
      emit('navigate', savedStep);
    }
    
    // Validate the data object
    if (!validFormData.data || typeof validFormData.data !== 'object') {
      console.error('[PolicyChangeForm] Invalid or missing form data in storage');
      console.error('[PolicyChangeForm] Data type:', Object.prototype.toString.call(validFormData.data));
      return;
    }
    
    // Check if the data is empty - nothing to restore
    const isEmptyFormData = Object.keys(validFormData.data).length === 0;
    if (isEmptyFormData) {
      console.warn('[PolicyChangeForm] Form data is empty, nothing to restore');
      return;
    }
    
    // Check if data has any meaningful values
    const hasValues = Object.values(validFormData.data).some(value => {
      if (typeof value === 'string') return value.trim().length > 0;
      if (typeof value === 'object' && value !== null) return Object.keys(value).length > 0;
      return !!value; // handle other types
    });
    
    if (!hasValues) {
      console.warn('[PolicyChangeForm] Form data has no meaningful values, nothing to restore');
      return;
    }
    
    // Update the storage if needed
    if (validFormData !== formStorage.value) {
      console.log('[PolicyChangeForm] Updating storage with validated restore data');
      formStorage.value = validFormData;
    }
    
    // Emit the restored data
    emit('restore-form', validFormData.data);
    console.log('[PolicyChangeForm] Form data restored successfully');
  } catch (error) {
    console.error('[PolicyChangeForm] Error restoring form:', error);
    console.error('[PolicyChangeForm] Error details:', {
      message: error.message,
      stack: error.stack
    });
  }
};

const saveFormState = (formData) => {
  try {
    console.log('[PolicyChangeForm] Starting form state save operation');
    
    if (!formData || typeof formData !== 'object') {
      console.error('[PolicyChangeForm] Invalid form data provided to saveFormState:', formData);
      return;
    }
    
    // Create and validate timestamp
    const timestamp = Date.now();
    console.log(`[PolicyChangeForm] Saving form state with timestamp: ${timestamp}`);
    
    // Create the storage object with validation
    const storageObject = {
      lastUpdated: timestamp,
      data: formData,
      currentStep: props.currentStep
    };
    
    console.log('[PolicyChangeForm] Form state object to save:', storageObject);
    console.log('[PolicyChangeForm] Object type:', Object.prototype.toString.call(storageObject));
    
    try {
      // Double-check we're saving a valid object by serializing and parsing it
      const serialized = JSON.stringify(storageObject);
      const validated = JSON.parse(serialized);
      
      console.log('[PolicyChangeForm] Validated storage object through JSON serialization');
      
      // Save the data
      formStorage.value = validated;
      
      // Verify what was saved
      console.log('[PolicyChangeForm] Saved state now in storage:', formStorage.value);
      console.log('[PolicyChangeForm] Saved state type:', typeof formStorage.value);
      
      console.log('[PolicyChangeForm] Form state saved successfully');
    } catch (serializationError) {
      console.error('[PolicyChangeForm] Failed to serialize storage object:', serializationError);
      
      // Fallback: try to save the original object directly
      console.log('[PolicyChangeForm] Attempting to save original object directly');
      formStorage.value = storageObject;
    }
  } catch (error) {
    console.error('[PolicyChangeForm] Error saving form state:', error);
    console.error('[PolicyChangeForm] Error details:', {
      message: error.message, 
      stack: error.stack,
      formData: formData ? 'exists' : 'missing',
      formDataType: typeof formData
    });
  }
};

// Expose methods to parent component
defineExpose({
  // currentStep, // No longer exposing local currentStep
  formSubmitted,
  isSubmitting,
  errors,
  goToNextStep,
  goToPreviousStep,
  goBack,
  resetForm,
  setFormSubmitted: (value = true) => { formSubmitted.value = value; },
  setSubmitting: (value) => { isSubmitting.value = value; },
  saveFormState
});
</script>

<style scoped>
/* Form transitions */
.form-section-enter-active,
.form-section-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.form-section-enter-from,
.form-section-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style> 